/* MIRA Y MANO — AW26 theme behaviour. No dependencies. */
(() => {
  const root = document.documentElement;
  const bindOnce = (el, key) => { if (!el || el.dataset['bound' + key]) return false; el.dataset['bound' + key] = '1'; return true; };
  const trapFocus = (container, e) => {
    if (e.key !== 'Tab') return;
    const items = [...container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, summary')].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!reduceMotion && getComputedStyle(root).getPropertyValue('--motion').trim() !== '0') root.classList.add('motion-ok');

  /* Header: transparent over the hero until scrolled */
  const initHeader = () => {
    const header = document.querySelector('[data-header-wrapper]');
    if (!header) return;
    const announcement = document.querySelector('.announcement');
    const setOffsets = () => {
      const a = announcement ? announcement.offsetHeight : 0;
      root.style.setProperty('--announcement-h', a + 'px');
      const wrapper = header.closest('.shopify-section') || header;
      if (header.classList.contains('is-overlay')) wrapper.style.top = Math.max(0, a - window.scrollY) + 'px';
    };
    const onScroll = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 40);
      setOffsets();
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', setOffsets);
  };

  /* Mobile menu drawer */
  const initDrawer = () => {
    document.querySelectorAll('[data-drawer-open]').forEach((btn) => {
      if (!bindOnce(btn, 'Drawer')) return;
      const drawer = document.getElementById(btn.getAttribute('aria-controls'));
      if (!drawer) return;
      const close = () => {
        drawer.classList.remove('is-open');
        btn.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
        btn.focus();
      };
      btn.addEventListener('click', () => {
        drawer.classList.add('is-open');
        btn.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
        const first = drawer.querySelector('button, a');
        if (first) first.focus();
      });
      drawer.querySelectorAll('[data-drawer-close]').forEach((el) => el.addEventListener('click', close));
      drawer.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); trapFocus(drawer, e); });
    });
  };

  /* Single fade-up on first entry */
  const initReveal = () => {
    // the motion layer (mym-motion.js) owns reveals when it is active
    if (root.classList.contains('mo') && document.body.dataset.moReveal === 'true') return;
    const els = document.querySelectorAll('.reveal');
    if (!('IntersectionObserver' in window) || reduceMotion) { els.forEach((el) => el.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add('is-in'); io.unobserve(entry.target); }
    }), { rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => io.observe(el));
  };

  /* Collection: facets auto-submit, drawer, density */
  const initFacets = () => {
    const form = document.querySelector('[data-facets-form]');
    if (bindOnce(form, 'Facets')) {
      form.addEventListener('change', (e) => {
        if (e.target.matches('input[type="number"]')) return;
        form.requestSubmit ? form.requestSubmit() : form.submit();
      });
    }
    const sort = document.querySelector('[data-sort-select]');
    if (bindOnce(sort, 'Sort')) sort.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
    const panel = document.querySelector('[data-facets-panel]');
    const openToggle = document.querySelector('.toolbar [data-facets-toggle]');
    const setPanel = (open) => {
      if (!panel) return;
      panel.classList.toggle('is-open', open);
      document.body.style.overflow = open ? 'hidden' : '';
      if (open) { const f = panel.querySelector('button, input'); if (f) f.focus(); } else if (openToggle) openToggle.focus();
    };
    document.querySelectorAll('[data-facets-toggle]').forEach((btn) => {
      if (bindOnce(btn, 'Toggle')) btn.addEventListener('click', () => setPanel(!panel.classList.contains('is-open')));
    });
    if (bindOnce(panel, 'Panel')) panel.addEventListener('keydown', (e) => {
      if (!panel.classList.contains('is-open')) return;
      if (e.key === 'Escape') setPanel(false);
      trapFocus(panel, e);
    });
    const grid = document.querySelector('[data-density-grid]');
    document.querySelectorAll('[data-density]').forEach((btn) => bindOnce(btn, 'Density') && btn.addEventListener('click', () => {
      if (!grid) return;
      document.querySelectorAll('[data-density]').forEach((b) => { b.classList.remove('is-active'); b.setAttribute('aria-pressed', 'false'); });
      btn.classList.add('is-active');
      btn.setAttribute('aria-pressed', 'true');
      grid.style.setProperty('--cols-desktop', btn.dataset.density);
      try { localStorage.setItem('mym-density', btn.dataset.density); } catch (e) { /* storage unavailable */ }
    }));
    try {
      const saved = localStorage.getItem('mym-density');
      const btn = saved && document.querySelector(`[data-density="${saved}"]`);
      if (btn) btn.click();
    } catch (e) { /* storage unavailable */ }
  };

  /* Product: variant picker, gallery counter, sticky add-to-bag, AJAX add */
  const initProduct = () => {
    document.querySelectorAll('[data-product]').forEach((section) => {
      if (!bindOnce(section, 'Product')) return;
      const dataEl = section.querySelector('[data-product-json]');
      if (!dataEl) return;
      const product = JSON.parse(dataEl.textContent);
      const form = section.querySelector('[data-product-form]');
      const idInput = form && form.querySelector('input[name="id"]');
      const addBtn = form && form.querySelector('[data-add-button]');
      const priceEl = section.querySelector('[data-price]');
      const errorEl = section.querySelector('[data-error]');
      const stickyBtn = section.querySelector('[data-sticky-add]');
      const stickyPrice = section.querySelector('[data-sticky-price]');
      const money = (cents) => {
        const fmt = section.dataset.moneyFormat || '{{amount}}';
        const match = fmt.match(/\{\{\s*(amount[a-z_]*)\s*\}\}/);
        const kind = match ? match[1] : 'amount';
        const decimals = kind.includes('no_decimals') ? 0 : 2;
        let value = (cents / 100).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
        if (kind.includes('comma_separator')) value = value.replace(/,/g, '#').replace('.', ',').replace(/#/g, '.');
        value = value.replace(/[.,]00$/, '');
        return section.dataset.currencyLabel === 'ar' ? `${value}\u00a0ر.س` : `SR\u00a0${value}`;
      };

      const update = () => {
        const selected = [...section.querySelectorAll('[data-option-index]:checked')].map((i) => i.value);
        section.querySelectorAll('[data-option-value-label]').forEach((el) => {
          const checked = section.querySelector(`[data-option-index="${el.dataset.optionValueLabel}"]:checked`);
          if (checked) el.textContent = checked.value;
        });
        const variant = product.variants.find((v) => v.options.every((o, i) => o === selected[i]));
        // mark unavailable combinations
        section.querySelectorAll('[data-option-index]').forEach((input) => {
          const idx = Number(input.dataset.optionIndex);
          const exists = product.variants.some((v) => v.available && v.options[idx] === input.value &&
            v.options.every((o, i) => i === idx || o === selected[i]));
          input.classList.toggle('is-unavailable', !exists);
        });
        if (!variant) {
          if (addBtn) { addBtn.disabled = true; addBtn.textContent = window.theme.strings.unavailable; }
          if (stickyBtn) stickyBtn.disabled = true;
          return;
        }
        if (idInput) idInput.value = variant.id;
        if (addBtn) {
          addBtn.disabled = !variant.available;
          addBtn.textContent = variant.available ? window.theme.strings.addToCart : window.theme.strings.soldOut;
        }
        if (stickyBtn) stickyBtn.disabled = !variant.available;
        if (stickyPrice) stickyPrice.textContent = money(variant.price);
        if (priceEl) {
          const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
          priceEl.classList.toggle('price--on-sale', !!onSale);
          priceEl.innerHTML = `<span class="price__current">${money(variant.price)}</span>${onSale ? `<s class="price__compare">${money(variant.compare_at_price)}</s>` : ''}`;
        }
        if (variant.featured_media) {
          const media = section.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (media) media.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      };
      section.querySelectorAll('[data-option-index]').forEach((input) => input.addEventListener('change', update));

      if (form && addBtn) {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          if (errorEl) errorEl.hidden = true;
          addBtn.setAttribute('aria-busy', 'true');
          try {
            const res = await fetch(window.theme.routes.cart_add_url + '.js', {
              method: 'POST',
              headers: { Accept: 'application/json' },
              body: new FormData(form)
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.description || data.message);
            addBtn.textContent = window.theme.strings.added;
            const cart = await (await fetch(window.theme.routes.cart_url + '.js')).json();
            document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
            setTimeout(() => { addBtn.textContent = window.theme.strings.addToCart; }, 2400);
          } catch (err) {
            if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
          } finally {
            addBtn.removeAttribute('aria-busy');
          }
        });
      }
      if (stickyBtn && addBtn) stickyBtn.addEventListener('click', () => addBtn.click());

      const gallery = section.querySelector('[data-gallery]');
      const counter = section.querySelector('[data-gallery-count]');
      if (gallery && counter) gallery.addEventListener('scroll', () => {
        const index = Math.round(Math.abs(gallery.scrollLeft) / gallery.clientWidth) + 1;
        counter.textContent = `${index} / ${gallery.children.length}`;
      }, { passive: true });

      const bar = section.querySelector('[data-sticky-atc]');
      if (bar && addBtn && 'IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        }).observe(addBtn);
      }
    });
  };

  /* Cart quantity steppers */
  const initCart = () => {
    document.querySelectorAll('select[data-default]').forEach((select) => {
      if (select.dataset.default) select.value = select.dataset.default;
    });
    document.querySelectorAll('[data-qty]').forEach((wrap) => {
      if (!bindOnce(wrap, 'Qty')) return;
      const input = wrap.querySelector('input');
      wrap.querySelectorAll('button').forEach((btn) => btn.addEventListener('click', () => {
        const next = Math.max(0, Number(input.value) + Number(btn.dataset.step));
        input.value = next;
        input.form && (input.form.requestSubmit ? input.form.requestSubmit() : input.form.submit());
      }));
    });
  };


  /* Header menus: click/tap toggles for top-level items without a page and for 3rd-level groups (desktop and drawer) */
  const initMenus = () => {
    const setSub = (btn, open) => {
      const list = document.getElementById(btn.getAttribute('aria-controls'));
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (list) list.hidden = !open;
    };
    const closeItem = (item) => {
      item.classList.remove('is-open');
      const t = item.querySelector('[data-nav-toggle]');
      if (t) t.setAttribute('aria-expanded', 'false');
      item.querySelectorAll('[data-sub-toggle][aria-expanded="true"]').forEach((b) => setSub(b, false));
    };
    document.querySelectorAll('[data-sub-toggle]').forEach((btn) => {
      if (!bindOnce(btn, 'Sub')) return;
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') !== 'true';
        const group = btn.closest('.header__dropdown, .menu-drawer__nav ul');
        if (open && group) group.querySelectorAll('[data-sub-toggle][aria-expanded="true"]').forEach((b) => { if (b !== btn) setSub(b, false); });
        setSub(btn, open);
        const item = btn.closest('.header__nav-item');
        if (item && open) item.classList.add('is-open');
      });
    });
    document.querySelectorAll('[data-nav-toggle]').forEach((btn) => {
      if (!bindOnce(btn, 'Nav')) return;
      const item = btn.closest('.header__nav-item');
      btn.addEventListener('click', () => {
        const open = !item.classList.contains('is-open');
        document.querySelectorAll('.header__nav-item.is-open').forEach((i) => { if (i !== item) closeItem(i); });
        if (open) { item.classList.add('is-open'); btn.setAttribute('aria-expanded', 'true'); } else closeItem(item);
      });
    });
    document.querySelectorAll('.header__nav-item').forEach((item) => {
      if (!bindOnce(item, 'NavItem')) return;
      item.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const openSub = item.querySelector('[data-sub-toggle][aria-expanded="true"]');
        if (openSub) { setSub(openSub, false); openSub.focus(); return; }
        closeItem(item);
        const top = item.querySelector('.header__nav-link');
        if (top) { top.focus(); item.classList.add('is-dismissed'); }
      });
      item.addEventListener('mouseleave', () => { if (!item.contains(document.activeElement)) closeItem(item); item.classList.remove('is-dismissed'); });
      item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) { closeItem(item); item.classList.remove('is-dismissed'); } });
    });
    document.addEventListener('click', (e) => {
      document.querySelectorAll('.header__nav-item.is-open').forEach((i) => { if (!i.contains(e.target)) closeItem(i); });
    });
    const drawer = document.getElementById('MenuDrawer');
    if (drawer && bindOnce(drawer, 'SubEsc')) {
      drawer.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const openSub = drawer.querySelector('[data-sub-toggle][aria-expanded="true"]');
        if (openSub && openSub.parentElement.contains(document.activeElement)) { e.stopPropagation(); setSub(openSub, false); openSub.focus(); }
      }, true);
    }
  };

  const init = () => { initHeader(); initMenus(); initDrawer(); initReveal(); initFacets(); initProduct(); initCart(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', () => { initReveal(); initProduct(); initFacets(); });
})();
